import type { ReactNode } from 'react';

import type { BlockNode, BlockTree } from './types';

/**
 * The read side of the block subsystem. **Bootstrap ships the registry empty**
 * — see `types.ts` for why the v1 block set is not declared here.
 *
 * The client attempts **no** sanitisation of its own: the body round-trips
 * through the server's sanitiser, and a second sanitiser that disagrees with
 * the first is worse than none. No block renderer may use
 * `dangerouslySetInnerHTML` on a stored string.
 */

export type BlockRendererFn = (block: BlockNode) => ReactNode;

export const blockRenderers: Record<string, BlockRendererFn> = {};

export function registerBlockRenderer(type: string, render: BlockRendererFn): void {
  blockRenderers[type] = render;
}

/**
 * An unregistered type renders nothing and says so in dev. It is never dropped
 * from the tree — the operator's document keeps it, and a deploy that adds the
 * renderer brings it back.
 */
export function RenderBlocks({ blocks }: { blocks: BlockTree }): ReactNode {
  return blocks.map((block) => {
    const render = blockRenderers[block.type];

    if (!render) {
      if (import.meta.env.DEV) {
        console.warn(`No renderer registered for block type "${block.type}".`);
      }
      return null;
    }

    return <div key={block.id}>{render(block)}</div>;
  });
}
